import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBarPreference, updateBarPreference, fetchBarPreferenceById } from './barPreferenceSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const BarPreferenceCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { barPreferenceId } = useParams();
  const loading = useSelector((state) => state.barPreference.loading);
  const error = useSelector((state) => state.barPreference.error);
  const barPreference = useSelector((state) => 
    barPreferenceId ? state.barPreference.items.find(b => b._id === barPreferenceId) : null
  );

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');

  useEffect(() => {
    if (barPreferenceId) {
      dispatch(fetchBarPreferenceById(barPreferenceId));
    }
  }, [barPreferenceId, dispatch]);

  useEffect(() => {
    if (barPreference && barPreference.image) {
      setExistingImage(getImageUrl(barPreference.image));
    }
  }, [barPreference]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && barPreferenceId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (barPreferenceId) {
        await dispatch(updateBarPreference({ id: barPreferenceId, formData: processedData })).unwrap();
        toast.success('Bar preference updated successfully!');
        log.info('Bar preference updated:', formData.label);
      } else {
        await dispatch(createBarPreference(processedData)).unwrap();
        toast.success('Bar preference created successfully!');
        log.info('Bar preference created:', formData.label);
      }
      navigate(paths.private.barPreference.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating bar preference:', err);
    }
  };

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter bar preference label'
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !barPreferenceId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && barPreference?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = barPreference ? {
    label: barPreference.label,
    image: [],
    status: barPreference.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {barPreferenceId ? 'Edit Bar Preference' : 'Create Bar Preference'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={barPreferenceId ? 'Update Bar Preference' : 'Create Bar Preference'}
      />
    </div>
  );
};

export default BarPreferenceCreateEdit; 